import React, { FunctionComponent, useCallback } from 'react';
import IPost from '@interfaces/IPost';
import cn from 'classnames';
import {
  CalendarOutlined,
  CommentOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  HeartOutlined,
  LinkOutlined,
  LoadingOutlined,
  ZoomInOutlined,
} from '@ant-design/icons';
import { FULL_TIME_FORMAT } from '@config/format';
import moment from 'moment';
import { setList as setGameList } from '@stores/gamesSelector';
import TextWithHashtags from '@components/TextWithHashtags';
import { Typography } from 'antd';
import { Link } from 'react-router-dom';
import { formatPostUrl, getPath, goTo } from '@helpers/navigation';
import { setPost } from '@stores/post';
import UserShortCard from '@components/UserShortCard';
import SpamPanel from '../SpamPanel';
import { get } from 'lodash';
import { block, unblock, hidePost, unhidePost, facePost, removefacePost } from '@stores/posts';
import useTranslate from '@hooks/useTranslate';
import componentContent from './PostCard.module.json';
import ViewFiles from '@components/ViewFiles/ViewFiles';
import useModal from '@hooks/useModal';
import ImageModal from '@components/FullImageModal/FullImageModal';
import ShowMore from '@fields/ShowMore/ShowMore';
import useGuards from '@hooks/useGuards';
import css from './PostCard.module.less';

interface PostCardProps {
  post: IPost;
  mode?: 'default' | 'spam';
  isHidingPost?: boolean;
  isUnhidingPost?: boolean;
  isApproving?: boolean;
  isCancelling?: boolean;
  isBlocking?: boolean;
  isUnblocking?: boolean;
  isSettingFacePost?: boolean;
  isRemovingFacePost?: boolean;
  disableModerationPanel?: boolean;
}

const PostCard: FunctionComponent<PostCardProps> = ({
  post,
  mode,
  isHidingPost,
  isUnhidingPost,
  isBlocking,
  isUnblocking,
  isSettingFacePost,
  isRemovingFacePost,
  disableModerationPanel,
}) => {
  const { allowChange, allowModerate } = useGuards();

  const blockPost = useCallback(() => {
    !isBlocking &&
      block({
        postId: post.id,
        preloaderId: `block_${post.id}`,
      });
  }, [isBlocking, post.id]);

  const unblockPost = useCallback(() => {
    !isUnblocking &&
      unblock({
        postId: post.id,
        preloaderId: `unblock_${post.id}`,
      });
  }, [isUnblocking, post.id]);

  const hidePostHandler = useCallback(() => {
    !isHidingPost &&
      hidePost({
        postId: post.id,
        preloaderId: `hidePost_${post.id}`,
      });
  }, [post, isHidingPost]);

  const unhidePostHandler = useCallback(() => {
    !isUnhidingPost &&
      unhidePost({
        postId: post.id,
        preloaderId: `unhidePost_${post.id}`,
      });
  }, [post, isUnhidingPost]);
  const facePostHandler = useCallback(async () => {
    if (!isHidingPost) {
      await hidePost({
        postId: post.id,
        preloaderId: `hidePost_${post.id}`,
      });
    }

    if (!isSettingFacePost) {
      facePost({
        postId: post.id,
        preloaderId: `facePost_${post.id}`,
      });
    }
  }, [post, isSettingFacePost]);

  const removefacePostHandler = useCallback(() => {
    !isRemovingFacePost &&
      removefacePost({
        postId: post.id,
        preloaderId: `removefacePost_${post.id}`,
      });
  }, [post, isRemovingFacePost]);

  const content = useTranslate(componentContent);
  const isModeSpam = mode === 'spam';

  const [isVisible, openModal, closeModal] = useModal();

  if (!post) return null;
  const filterByUser = () => goTo('userDetail', { userId: post.author.id });
  const filterByTag = (hashtag: string) => goTo('postList', undefined, { hashtag });
  const filterByGame = () => {
    setGameList([post.game]);
    goTo('postList', undefined, { gameId: post.game.id });
  };
  const postUrl = post.mobileUrlPost ? formatPostUrl(post.mobileUrlPost) : null;

  return (
    <div className={css.root}>
      <div className={cn(css.images, css.notSpam)}>
        {
          <>
            <div
              className={css.overlay}
              onClick={openModal}
            >
              <div className={css.overlayItem}>
                <ZoomInOutlined />
              </div>
            </div>
            <ViewFiles
              files={post.files}
              altText={post.text}
            />
          </>
        }
      </div>
      <div className={css.content}>
        <div className={css.header}>
          <div className={css.headerUserName}>
            <UserShortCard
              user={post.author}
              onClick={filterByUser}
            />
            <div className={css.meta}>
              <span className={css.metaIcon}>
                <CalendarOutlined />
              </span>
              <span>{moment(post.createdDateTime).format(FULL_TIME_FORMAT)}</span>
            </div>
          </div>
          <div className={cn(css.metaLine, css.meta)}>
            <Link
              to={getPath('postDetailLikes', { postId: post.id })}
              className={cn(css.metaLineItem, 'clickable')}
              onClick={() => setPost(post)}
            >
              <span className={css.metaIcon}>
                <HeartOutlined />
              </span>
              <span>{post.likesCount}</span>
            </Link>
            <Link
              to={getPath('postDetail', { postId: post.id })}
              className={cn(css.metaLineItem, 'clickable')}
              onClick={() => setPost(post)}
            >
              <span className={css.metaIcon}>
                <CommentOutlined />
              </span>
              <span>{post.commentsCount}</span>
            </Link>
            <div className={css.metaLineItem}>
              <span className={css.metaIcon}>
                <EyeOutlined />
              </span>
              <span>{post.viewsCount || 0}</span>
            </div>
            <div className={css.metaLineItem}>
              <span
                className='clickable'
                onClick={filterByGame}
              >
                {get(post, ['game', 'name'], 'undefined')}
              </span>
            </div>
            {(allowChange || allowModerate) && !isModeSpam ? (
              <>
                <div className={css.metaLineItem}>
                  <span className={css.metaIcon}>
                    {isBlocking || isUnblocking ? (
                      <LoadingOutlined />
                    ) : post.isBlocked ? (
                      <EyeOutlined />
                    ) : (
                      <EyeInvisibleOutlined />
                    )}
                  </span>
                  {post.isBlocked ? (
                    <span
                      className='clickable'
                      onClick={unblockPost}
                    >
                      {content.unblock}
                    </span>
                  ) : (
                    <span
                      className='clickable'
                      onClick={blockPost}
                    >
                      {content.block}
                    </span>
                  )}
                </div>
                <div className={css.metaLineItem}>
                  <span className={css.metaIcon}>
                    {(isHidingPost || isUnhidingPost) && <LoadingOutlined />}
                  </span>
                  {post.isHidden ? (
                    <span
                      className='clickable'
                      onClick={unhidePostHandler}
                    >
                      {content.unshadowban}
                    </span>
                  ) : (
                    <span
                      className='clickable'
                      onClick={hidePostHandler}
                    >
                      {content.shadowban}
                    </span>
                  )}
                </div>
                <div className={css.metaLineItem}>
                  <span className={css.metaIcon}>
                    {(isSettingFacePost || isRemovingFacePost) && <LoadingOutlined />}
                  </span>
                  {post.marks?.length &&
                  post.marks?.filter((item) => item?.markType === 'FACE').length ? (
                    <span
                      className='clickable'
                      onClick={removefacePostHandler}
                    >
                      {content.notface}
                    </span>
                  ) : (
                    <span
                      className='clickable'
                      onClick={facePostHandler}
                    >
                      {content.face}
                    </span>
                  )}
                </div>
              </>
            ) : null}
          </div>
          <div>
            {post.playerID ? (
              <div className={css.gameId}>
                <span>{content.nickname}: </span>
                <span>{post.playerID.contact}</span>
              </div>
            ) : null}
            {postUrl ? (
              <div className={cn(css.metaLine, css.meta)}>
                <Typography.Link
                  copyable={{ text: postUrl }}
                  href={postUrl}
                  target='_blank'
                  className={cn(css.metaLineItem, 'clickable')}
                >
                  <span className={css.metaIcon}>
                    <LinkOutlined />
                  </span>
                  <span>{postUrl}</span>
                </Typography.Link>
              </div>
            ) : null}
          </div>
        </div>

        <div className={css.body}>
          <ShowMore height={isModeSpam ? 174 : 184}>
            <TextWithHashtags
              onHashtagClick={filterByTag}
              source={post.tags}
            >
              {post.text}
            </TextWithHashtags>
          </ShowMore>
        </div>

        <div className={css.footer}>
          {isModeSpam ? (
            <SpamPanel
              disabled={disableModerationPanel}
              isHidingPost={!!isHidingPost}
              onHidePost={hidePostHandler}
              isUnhidingPost={!!isUnhidingPost}
              onUnhidePost={unhidePostHandler}
              isBlocking={!!isBlocking}
              isUnblocking={!!isUnblocking}
              onBlock={blockPost}
              onUnblock={unblockPost}
              isSettingFacePost={!!isSettingFacePost}
              isRemovingFacePost={!!isRemovingFacePost}
              onFacePost={facePostHandler}
              onRemovefacePost={removefacePostHandler}
              item={post}
              mode='post'
            />
          ) : null}
        </div>
      </div>
      {isVisible ? (
        <ImageModal
          onClose={closeModal}
          files={post.files}
        />
      ) : null}
    </div>
  );
};

export default PostCard;
